import { BehaviorSubject, PartialObserver, Subscription } from 'rxjs';
import { IServerSideGetRowsParams, IServerSideDatasource } from '@ag-grid-community/core';
import { LuceneExpression, toString as luceneToString } from 'lucene';

import { OrderBy, OrderByDirection } from '../utils/constant';
import { DatacenterViewService } from '../services/datacenter-view.service';
import { DatacenterFilterUtilsService } from '../services/datacenter-filter-utils.service';
import { FilterType, FilterModel } from '../models/ag-grid';
import {
	createDimensionFilter,
	createReferenceItemFilter,
	createNumberFilter,
	createExactNumberFilter,
	createDateFilter,
	createTextFilter,
	createDateRangeFilter,
	createDropDownFilter,
	createDocumentTextFilter,
	createDocumentDateFilter,
	createDocumentReferenceItemFilter,
	createDocumentMillDropDownFilter,
	createDocumentDateRangeFilter,
	createSuggestiveListFilter,
	createShipmentDimensionFilter,
	createShipmentMultiValueDateFilter,
	createProducedDimensionFilter,
	createShipmentMultiValueDropDownFilter,
	createShipmentMultiValueNumberFilter,
	createShipmentMultiValueTextFilter
} from '../services/datacenter-filter';
import { UserDatacenterView } from '../models/datacenter-aggrid-view';

import { ColDefWithFields } from './column-type';
import { ContextDatacenter } from './types/context-datacenter';

export class DataSource implements IServerSideDatasource {
	sub: Subscription[] = [];
	dataCentersToApplyNotNullCondition = ['users', 'organization'];
	private readonly rowsCountSubject = new BehaviorSubject(<number | undefined> undefined);

	private readonly rowsOrderBySubject = new BehaviorSubject(<OrderBy<string>[] | undefined> undefined);

	private readonly rowsFilterSubject = new BehaviorSubject(<string | undefined> undefined);

	constructor(
		readonly contextDatacenter: ContextDatacenter,
		readonly viewService: DatacenterViewService,
		readonly filterService: DatacenterFilterUtilsService,
		public currentColumns: string[],
		public currentView: UserDatacenterView,
	) {}

	subscribeToRowsCount(observer: PartialObserver<number | undefined>) {
		return this.rowsCountSubject.subscribe(observer);
	}

	subscribeToRowsOrderBy(observer: PartialObserver<OrderBy<string>[] | undefined>) {
		return this.rowsOrderBySubject.subscribe(observer);
	}

	subscribeToRowsFilter(observer: PartialObserver<string | undefined>) {
		return this.rowsFilterSubject.subscribe(observer);
	}

	destroy() {
		for (const sub of this.sub) {
			sub.unsubscribe();
		}
	}

	getRows(params: IServerSideGetRowsParams<Record<string, unknown>>) {
		const { api, request } = params;
		let criterionFilterNode;
		this.rowsFilterSubject.next(undefined);
		this.rowsOrderBySubject.next(undefined);
		this.rowsCountSubject.next(undefined);
		for (const [columnId, filterModel] of Object.entries(<Record<string, FilterModel>> request.filterModel)) {
			const colDef = api.getColumnDef(columnId);
			const colId = colDef?.colId;
			const { fields = colDef?.field ? [colDef.field] : undefined } = <ColDefWithFields> colDef;
			const field = colDef?.field ?? (fields ? fields[0] : undefined);
			const { multiFilterCodes } = <ColDefWithFields> api.getColumnDef(columnId);
			const { applyLikeCondition } = <ColDefWithFields> api.getColumnDef(columnId);
			let criterionFilterValueNode;
			switch (filterModel.customFilterName) {
			case FilterType.DIMENSION_FILTER:
				criterionFilterValueNode = createDimensionFilter(filterModel);
				break;
			case FilterType.SHIPMENT_DIMENSION_FILTER:
				criterionFilterValueNode = createShipmentDimensionFilter(filterModel);
				break;
			case FilterType.PRODUCED_DIMENSION_FILTER:
				criterionFilterValueNode = createProducedDimensionFilter(filterModel);
				break;
			case FilterType.ORDER_NUMBER_FILTER:
				criterionFilterValueNode = createReferenceItemFilter(filterModel, fields);
				break;
			case FilterType.DOCUMENT_ORDER_NUMBER_FILTER:
				criterionFilterValueNode = createDocumentReferenceItemFilter(filterModel, fields);
				break;
			case FilterType.NUMBER_FILTER:
				criterionFilterValueNode = createNumberFilter(filterModel, field);
				break;
			case FilterType.SHIPMENT_MULTI_VALUE_NUMBER_FILTER:
				criterionFilterValueNode = createShipmentMultiValueNumberFilter(filterModel, field);
				break;
			case FilterType.EXACT_NUMBER_FILTER:
				criterionFilterValueNode = createExactNumberFilter(filterModel, field);
				break;
			case FilterType.DATE_FILTER:
				criterionFilterValueNode = createDateFilter(filterModel.date, field);
				break;
			case FilterType.SHIPMENT_MULTI_VALUE_DATE_FILTER:
				criterionFilterValueNode = createShipmentMultiValueDateFilter(
					filterModel.date,
					field
				);
				break;
			case FilterType.DOCUMENT_DATE_FILTER:
				criterionFilterValueNode = createDocumentDateFilter(filterModel.date, fields);
				break;
			case FilterType.DROPDOWN_FILTER:
				criterionFilterValueNode = createDropDownFilter(
					filterModel.text,
					colId,
					multiFilterCodes,
					applyLikeCondition,
					this.dataCentersToApplyNotNullCondition.includes(this.currentView.type)
				);
				break;
			case FilterType.SHIPMENT_MULTI_VALUE_DROPDOWN_FILTER:
				criterionFilterValueNode = createShipmentMultiValueDropDownFilter(
					filterModel.text,
					colId,
					multiFilterCodes,
					this.dataCentersToApplyNotNullCondition.includes(this.currentView.type)
				);
				break;
			case FilterType.SUGGESTIVE_LIST_FILTER:
				criterionFilterValueNode = createSuggestiveListFilter(filterModel.text, colId);
				break;
			case FilterType.DOCUMENT_MILL_DROPDOWN_FILTER:
				criterionFilterValueNode = createDocumentMillDropDownFilter(filterModel.text, fields);
				break;
			case FilterType.DATE_RANGE_FILTER:
				criterionFilterValueNode = createDateRangeFilter(filterModel, field);
				break;
			case FilterType.DOCUMENT_DATE_RANGE_FILTER:
				criterionFilterValueNode = createDocumentDateRangeFilter(filterModel, fields);
				break;
			case FilterType.TEXT_FILTER:
				criterionFilterValueNode = createTextFilter(filterModel, field);
				break;
			case FilterType.SHIPMENT_MULTI_VALUE_TEXT_FILTER:
				criterionFilterValueNode = createShipmentMultiValueTextFilter(filterModel, field);
				break;
			case FilterType.DOCUMENT_TEXT_FILTER:
				criterionFilterValueNode = createDocumentTextFilter(filterModel, fields);
				break;
			default:
				throw new Error('unknown filter type');
			}
			if (!criterionFilterNode) {
				criterionFilterNode = criterionFilterValueNode;
				continue;
			}
			criterionFilterNode = {
				left: criterionFilterNode,
				operator: 'AND',
				right: criterionFilterValueNode,
				parenthesized: false
			};
		}

		const { currentView } = this;
		if (this.filterService.getLuceneFromAllFilters(currentView)) {
			criterionFilterNode = !criterionFilterNode
				? this.filterService.getLuceneFromAllFilters(currentView)
				: {
					left: criterionFilterNode,
					operator: 'AND',
					right: this.filterService.getLuceneFromAllFilters(currentView),
					parenthesized: true,
				};
		}
		const filter = luceneToString(<LuceneExpression> criterionFilterNode);
		const orderBy: OrderBy<string>[] = [];
		for (const sortModel of <{ colId: string; sort: OrderByDirection }[]> request.sortModel) {
			const field = api.getColumnDef(sortModel.colId)?.field;
			if (field) {
				orderBy.push(new OrderBy(field, sortModel.sort));
				continue;
			}
			const { fields } = <{ fields?: string[] }> api.getColumnDef(sortModel.colId);
			if (!fields) {
				continue;
			}
			for (const fieldItem of fields) {
				orderBy.push(new OrderBy(fieldItem, sortModel.sort));
			}
		}
		this.rowsFilterSubject.next(filter);

		if (orderBy.length === 0) {
			orderBy.push(this.contextDatacenter.getDefaultOrderBy());
		}
		this.rowsOrderBySubject.next(orderBy);

		this.sub.push(
			this.contextDatacenter.getDataFromGrid(
				orderBy,
				this.currentColumns,
				filter,
				params,
				this.rowsCountSubject,
				api,
			)
		);
	}
}
